import React, { useState, useEffect } from 'react';
import { policiesAPI } from '../services/api';

function Policies() {
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showConsent, setShowConsent] = useState(null);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [formData, setFormData] = useState({
    policy_type: 'terms_of_service',
    title: '',
    content: '',
    version: '',
    effective_date: '',
    is_required: true,
    is_active: false,
  });
  const [policies, setPolicies] = useState([]);
  const [consentData, setConsentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const formatDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(value);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await policiesAPI.getPolicies();
      setPolicies(response.data.data || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsentStatus = async (policyId) => {
    try {
      const response = await policiesAPI.getConsentStatus(policyId);
      setConsentData(response.data);
      setShowConsent(policyId);
    } catch (error) {
      console.error('Error fetching consent status:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim() || !formData.version.trim()) {
      setMessage({ type: 'error', text: 'Title, content, and version are required' });
      return;
    }

    try {
      if (editingPolicyId) {
        await policiesAPI.updatePolicy(editingPolicyId, formData);
        setMessage({ type: 'success', text: 'Policy updated successfully!' });
      } else {
        await policiesAPI.createPolicy(formData);
        setMessage({ type: 'success', text: 'Policy created successfully!' });
      }
      
      const emptyForm = {
        policy_type: 'terms_of_service',
        title: '',
        content: '',
        version: '',
        effective_date: '',
        is_required: true,
        is_active: false,
      };
      
      // Reset form
      setFormData(emptyForm);
      setEditingPolicyId(null);
      
      setShowForm(false);
      fetchPolicies();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save policy' 
      });
    }
  };

  const handleActivate = async (policyId) => {
    if (!window.confirm('Are you sure you want to activate this policy? This will deactivate other policies of the same type.')) {
      return;
    }

    try {
      await policiesAPI.activatePolicy(policyId);
      setMessage({ type: 'success', text: 'Policy activated successfully!' });
      fetchPolicies();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to activate policy' 
      });
    }
  };

  const viewPolicyDetail = async (policyId) => {
    try {
      const response = await policiesAPI.getPolicyDetail(policyId);
      setShowDetail(response.data);
    } catch (error) {
      console.error('Error fetching policy detail:', error);
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicyId(policy.id);
    setMessage({ type: '', text: '' });
    setShowForm(true);
    const initForm = {
      policy_type: policy.policy_type || 'terms_of_service',
      title: policy.title || '',
      version: policy.version || '',
      effective_date: formatDateTimeLocal(policy.effective_date),
      is_required: Boolean(policy.is_required),
      is_active: Boolean(policy.is_active),
    };
    setFormData({
      ...initForm,
      content: '',
    });
    (async () => {
      try {
        const response = await policiesAPI.getPolicyDetail(policy.id);
        setFormData({
          ...initForm,
          content: response.data?.content || '',
        });
      } catch (error) {
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.detail || 'Failed to load policy content' 
        });
      }
    })();
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('정말 삭제하시겠습니까? 삭제하면 복구할 수 없습니다.')) {
      return;
    }

    try {
      await policiesAPI.deletePolicy(policyId);
      setMessage({ type: 'success', text: 'Policy deleted successfully!' });
      if (editingPolicyId === policyId) {
        setEditingPolicyId(null);
        setShowForm(false);
      }
      fetchPolicies();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete policy' 
      });
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingPolicyId(null);
    setFormData({
      policy_type: 'terms_of_service',
      title: '',
      content: '',
      version: '',
      effective_date: '',
      is_required: true,
      is_active: false,
    });
  };

  // Group policies by type
  const groupedPolicies = policies.reduce((acc, policy) => {
    if (!acc[policy.policy_type]) {
      acc[policy.policy_type] = [];
    }
    acc[policy.policy_type].push(policy);
    return acc;
  }, {});

  const policyTypeNames = {
    terms_of_service: 'Terms of Service',
    privacy_policy: 'Privacy Policy',
    marketing_consent: 'Marketing Consent',
    data_processing: 'Data Processing Agreement',
  };

  return (
    <div>
      <div className="page-header">
        <h1>정책 관리</h1>
        <p>개인 정보보호번 관련 관리 (개인정보보호법 준수)</p>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            if (showForm) {
              cancelForm();
            } else {
              setEditingPolicyId(null);
              setFormData({
                policy_type: 'terms_of_service',
                title: '',
                content: '',
                version: '',
                effective_date: '',
                is_required: true,
                is_active: false,
              });
              setShowForm(true);
            }
          }}
          style={{ marginTop: '10px' }}
        >
          {showForm ? 'Cancel' : '+ Create Policy'}
        </button>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>{editingPolicyId ? 'Edit Policy' : 'Create New Policy'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Policy Type *</label>
                <select
                  name="policy_type"
                  className="form-control"
                  value={formData.policy_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="terms_of_service">Terms of Service</option>
                  <option value="privacy_policy">Privacy Policy</option>
                  <option value="marketing_consent">Marketing Consent</option>
                  <option value="data_processing">Data Processing Agreement</option>
                </select>
              </div>

              <div className="form-group">
                <label>Version *</label>
                <input
                  type="text"
                  name="version"
                  className="form-control"
                  placeholder="e.g., 1.0, 2.1"
                  value={formData.version}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                className="form-control"
                placeholder="Policy title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                className="form-control"
                placeholder="Full policy content (supports HTML)"
                value={formData.content}
                onChange={handleInputChange}
                rows="10"
                required
              />
            </div>

            <div className="form-group">
              <label>Effective Date *</label>
              <input
                type="datetime-local"
                name="effective_date"
                className="form-control"
                value={formData.effective_date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="is_required"
                  checked={formData.is_required}
                  onChange={handleInputChange}
                />
                Required (users must accept)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (deactivates other versions)
              </label>
            </div>

            <button type="submit" className="btn btn-success">
              {editingPolicyId ? 'Update Policy' : 'Create Policy'}
            </button>
          </form>
        </div>
      )}

      {/* Policy Detail Modal */}
      {showDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>{showDetail.title}</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Version {showDetail.version} | {policyTypeNames[showDetail.policy_type]}
            </p>
            <div style={{ 
              whiteSpace: 'pre-wrap', 
              fontSize: '14px', 
              lineHeight: '1.6',
              marginBottom: '20px' 
            }}>
              {showDetail.content}
            </div>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowDetail(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Consent Statistics Modal */}
      {showConsent && consentData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>Consent Status - {consentData.policy.title}</h2>
            
            <div className="stats-grid" style={{ marginTop: '20px' }}>
              <div className="stat-card">
                <h3>Total Users</h3>
                <div className="value">{consentData.statistics.total_users}</div>
              </div>
              <div className="stat-card">
                <h3>Consented</h3>
                <div className="value">{consentData.statistics.consented_users}</div>
              </div>
              <div className="stat-card">
                <h3>Declined</h3>
                <div className="value">{consentData.statistics.declined_users}</div>
              </div>
              <div className="stat-card">
                <h3>Consent Rate</h3>
                <div className="value">{consentData.statistics.consent_rate}%</div>
              </div>
            </div>

            <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>Recent Activities</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Consented</th>
                    <th>Date</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {consentData.recent_activities.map((activity, idx) => (
                    <tr key={idx}>
                      <td>{activity.user_name}</td>
                      <td>{activity.user_email}</td>
                      <td>
                        <span className={`badge ${activity.consented ? 'badge-success' : 'badge-danger'}`}>
                          {activity.consented ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{new Date(activity.consent_date).toLocaleString()}</td>
                      <td style={{ fontSize: '11px' }}>{activity.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button 
              className="btn btn-secondary" 
              onClick={() => setShowConsent(null)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All Policies</h2>
        </div>
        
        {loading ? (
          <div className="loading">Loading policies...</div>
        ) : policies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No policies created yet
          </div>
        ) : (
          <div>
            {Object.entries(groupedPolicies).map(([type, typePolicies]) => (
              <div key={type} style={{ marginBottom: '30px' }}>
                <h3 style={{ 
                  padding: '10px 20px', 
                  background: '#ecf0f1', 
                  borderRadius: '4px',
                  marginBottom: '15px' 
                }}>
                  {policyTypeNames[type] || type}
                </h3>
                
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Required</th>
                        <th>Effective Date</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {typePolicies.map((policy) => (
                        <tr key={policy.id}>
                          <td>
                            <span className="badge badge-info">v{policy.version}</span>
                          </td>
                          <td>{policy.title}</td>
                          <td>
                            <span className={`badge ${policy.is_active ? 'badge-success' : 'badge-warning'}`}>
                              {policy.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            {policy.is_required ? (
                              <span className="badge badge-danger">Required</span>
                            ) : (
                              <span className="badge badge-info">Optional</span>
                            )}
                          </td>
                          <td>{new Date(policy.effective_date).toLocaleDateString()}</td>
                          <td>{new Date(policy.created_at).toLocaleDateString()}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px' }}>
                              <button
                                className="btn btn-primary"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => viewPolicyDetail(policy.id)}
                              >
                                View
                              </button>
                          {!policy.is_active && (
                                <button
                                  className="btn btn-success"
                                  style={{ padding: '5px 10px', fontSize: '12px' }}
                                  onClick={() => handleActivate(policy.id)}
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => fetchConsentStatus(policy.id)}
                              >
                                  Consent
                                </button>
                              <button
                                className="btn btn-primary"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => handleEditPolicy(policy)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-danger"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => handleDeletePolicy(policy.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h3>🔒 Compliance Note (개인정보보호법)</h3>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          This system tracks user consent for compliance with Korean Personal Information Protection Act (개인정보보호법).
          All consent records include timestamp, IP address, and user agent information for audit purposes.
        </p>
        <ul style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>Article 22: User consent tracking with timestamp and IP address</li>
          <li>Article 39-6: Policy version management and activation system</li>
          <li>Article 30: Audit logs for all consent activities</li>
        </ul>
      </div>
    </div>
  );
}

export default Policies;
